'use client';

import { MetaDefault } from '../../components/typography';
import { Slider } from '../../components/slider';
import { Box, VStack } from '@terra/ui-styled-system';
import { useEffect, useState } from 'react';

export type ScanEffortSliderOption<TValue extends string> = {
  value: TValue;
  label: string;
  description: string;
};

type ScanEffortSliderProps<TValue extends string> = {
  options: readonly ScanEffortSliderOption<TValue>[];
  value: TValue;
  ariaLabel: string;
  disabled?: boolean;
  onCommit: (value: TValue) => void;
};

export function ScanEffortSlider<TValue extends string>({
  options,
  value,
  ariaLabel,
  disabled,
  onCommit,
}: ScanEffortSliderProps<TValue>) {
  const valueIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const [draft, setDraft] = useState(valueIndex);

  useEffect(() => {
    setDraft(valueIndex);
  }, [valueIndex]);

  const draftOption = options[draft] ?? options[valueIndex];

  return (
    <VStack alignItems="stretch" gap={3} w="full">
      <Box px={6} pb={4}>
        <Slider
          size="sm"
          min={0}
          max={options.length - 1}
          step={1}
          value={[draft]}
          disabled={disabled}
          thumbAriaLabels={[ariaLabel]}
          marks={options.map((option, index) => ({
            value: index,
            label: option.label,
          }))}
          onValueChange={(details) => {
            const next = details.value[0];

            if (next == null) {
              return;
            }

            setDraft(next);
          }}
          onValueChangeEnd={(details) => {
            const nextIndex = Math.round(details.value[0] ?? draft);
            const nextOption = options[nextIndex];

            setDraft(nextIndex);

            if (nextOption && nextOption.value !== value) {
              onCommit(nextOption.value);
            }
          }}
        />
      </Box>
      {draftOption ? (
        <MetaDefault color="fg.muted">{draftOption.description}</MetaDefault>
      ) : null}
    </VStack>
  );
}
